/**
 * The pure parts of the registry-order picker: the option lists, what a
 * selection is, and how it becomes the request the API takes.
 */

import { JURISDICTIONS } from '@/constants';
import type { RegistryOrderSubject } from '@/hooks/useRegistryOrderSubjects';
import {
  describeRegistryPrice,
  type RegistryCompanyCandidate,
  type RegistryOrderCreateRequest,
  type RegistryProduct,
} from '@/types/registry-documents';

/** Analyst notes are capped by the API; a manual order's composed brief
 * shares the same ceiling, so the field stops well short of it. */
export const NOTES_MAX_LENGTH = 1500;

/** How the company is identified: found in Armadillo's index, or described
 * by hand because the search cannot find it. */
export type CompanyMode = 'search' | 'manual';

export interface ManualFormValues {
  companyName: string;
  registrationNumber: string;
  address: string;
  town: string;
  postcode: string;
}

export const EMPTY_MANUAL: ManualFormValues = {
  companyName: '',
  registrationNumber: '',
  address: '',
  town: '',
  postcode: '',
};

export const COUNTRY_OPTIONS = [...JURISDICTIONS]
  .sort((a, b) => a.name.localeCompare(b.name))
  .map((country) => ({
    value: country.alpha2code,
    label: `${country.name} (${country.alpha2code})`,
  }));

export function countryNameFor(code: string | null): string {
  return JURISDICTIONS.find((entry) => entry.alpha2code === code)?.name ?? code ?? '—';
}

/** The manual form as a subject prefills it. */
export function manualValuesFor(subject: RegistryOrderSubject): ManualFormValues {
  return {
    ...EMPTY_MANUAL,
    companyName: subject.name,
    registrationNumber: subject.registrationNumber ?? '',
    address: subject.address ?? '',
  };
}

/** Everything the analyst chose across the three steps. */
export interface OrderSelection {
  reportId: string;
  subjectId: string | null;
  country: string | null;
  mode: CompanyMode;
  candidate: RegistryCompanyCandidate | null;
  manual: ManualFormValues;
  products: string[];
  notes: string;
}

function blankToNull(value: string): string | null {
  return value.trim() || null;
}

/**
 * The request for a selection. A searched company goes by Armadillo's
 * `company_id`; a manual one goes without it, by name and details, and the
 * backend composes the research desk's brief from them.
 */
export function buildOrderPayload(
  selection: OrderSelection,
  allowDuplicate: boolean
): RegistryOrderCreateRequest {
  const base: RegistryOrderCreateRequest = {
    report_id: selection.reportId,
    subject_id: selection.subjectId,
    country: selection.country ?? '',
    products: selection.products,
    notes: blankToNull(selection.notes),
    allow_duplicate: allowDuplicate,
    company_name: '',
  };
  if (selection.mode === 'search' && selection.candidate) {
    return {
      ...base,
      company_id: selection.candidate.companyId,
      company_name: selection.candidate.name,
    };
  }
  const { manual } = selection;
  return {
    ...base,
    company_name: manual.companyName.trim(),
    registration_number: blankToNull(manual.registrationNumber),
    address: blankToNull(manual.address),
    town: blankToNull(manual.town),
    postcode: blankToNull(manual.postcode),
  };
}

export interface FirmTotal {
  amount: number;
  currency: string | null;
}

function hasFirmPrice(product: RegistryProduct): boolean {
  return !product.requiresQuote && product.price != null && Boolean(product.currency);
}

/**
 * The list price of a selection, by the backend's rule: a total only when
 * every line carries a firm price in one currency. A quote line, even with
 * an indicative figure, means no total rather than a partial one.
 */
export function firmTotal(products: RegistryProduct[]): FirmTotal | null {
  const currencies = new Set(products.map((product) => product.currency));
  const summable = products.length > 0 && products.every(hasFirmPrice) && currencies.size === 1;
  if (!summable) {
    return null;
  }
  return {
    amount: products.reduce((sum, product) => sum + (product.price ?? 0), 0),
    currency: products[0].currency,
  };
}

// ---------------------------------------------------------------------------
// Ordering for every company at once
// ---------------------------------------------------------------------------

/** One company in a batch: the subject it comes from and the country whose
 * registry holds its documents. */
export interface BatchCompany {
  subject: RegistryOrderSubject;
  country: string;
}

/** Firm prices of one document across countries, in one currency. */
export interface PriceRange {
  min: number;
  max: number;
  currency: string | null;
}

/** A document offered in every country of a batch, as the shared list
 * shows it: one row, priced across the countries. */
export interface SharedProduct {
  code: string;
  description: string | null;
  /** Null when no country prices it firmly, or the firm prices are not in
   * one currency - the per-country totals say what each order costs. */
  priceRange: PriceRange | null;
  /** At least one country quotes it: that order gets a quote email first. */
  anyQuote: boolean;
  /** The country's own catalogue line, for pricing that country's order. */
  perCountry: Record<string, RegistryProduct>;
}

/** A document some but not all of the countries offer, left off the list. */
export interface OmittedProduct {
  code: string;
  /** The countries whose catalogue does not carry it. */
  missingIn: string[];
}

function priceRangeOf(lines: RegistryProduct[]): PriceRange | null {
  const firm = lines.filter(hasFirmPrice);
  const currencies = new Set(firm.map((line) => line.currency));
  if (firm.length === 0 || currencies.size !== 1) {
    return null;
  }
  const prices = firm.map((line) => line.price ?? 0);
  return { min: Math.min(...prices), max: Math.max(...prices), currency: firm[0].currency };
}

/**
 * The documents every country in a batch offers, by code: the intersection
 * of the catalogues, with the codes left out and where they are missing.
 * Codes keep the order of their first appearance so the list reads like
 * the first catalogue. Empty catalogues give an empty intersection.
 */
export function sharedProducts(catalogues: Record<string, RegistryProduct[]>): {
  offered: SharedProduct[];
  omitted: OmittedProduct[];
} {
  const countries = Object.keys(catalogues);
  const codes = new Set(countries.flatMap((country) => catalogues[country].map((p) => p.code)));
  const offered: SharedProduct[] = [];
  const omitted: OmittedProduct[] = [];
  for (const code of codes) {
    const perCountry: Record<string, RegistryProduct> = {};
    const missingIn: string[] = [];
    for (const country of countries) {
      const line = catalogues[country].find((entry) => entry.code === code);
      if (line) {
        perCountry[country] = line;
      } else {
        missingIn.push(country);
      }
    }
    if (missingIn.length > 0) {
      omitted.push({ code, missingIn });
      continue;
    }
    const lines = Object.values(perCountry);
    offered.push({
      code,
      description: lines.find((line) => line.description)?.description ?? null,
      priceRange: priceRangeOf(lines),
      anyQuote: lines.some((line) => line.requiresQuote),
      perCountry,
    });
  }
  return { offered, omitted };
}

/** A shared product's price for display: one figure when the countries
 * agree, a range when they differ, "Quote" when no country prices it. */
export function describeSharedPrice(product: SharedProduct): string | null {
  const { priceRange } = product;
  if (!priceRange) {
    return product.anyQuote ? 'Quote' : null;
  }
  const min = describeRegistryPrice(priceRange.min, priceRange.currency, false);
  if (priceRange.min === priceRange.max) {
    return min;
  }
  return `${min} - ${describeRegistryPrice(priceRange.max, priceRange.currency, false)}`;
}

export interface CountryTotal {
  country: string;
  total: FirmTotal | null;
}

/**
 * What the batch costs at list price: one firm total per country (what
 * each order in that country costs, by the single order's rule) and the
 * grand total over the companies - only when every order has a firm total
 * in one currency, as a partial sum would understate the batch.
 */
export function batchTotals(
  companies: BatchCompany[],
  chosen: SharedProduct[]
): { perCountry: CountryTotal[]; grand: FirmTotal | null } {
  const countries = [...new Set(companies.map((company) => company.country))];
  const perCountry = countries.map((country) => ({
    country,
    total: firmTotal(
      chosen.map((product) => product.perCountry[country]).filter((line) => line !== undefined)
    ),
  }));
  const totals = companies.map(
    (company) => perCountry.find((entry) => entry.country === company.country)?.total ?? null
  );
  const firm = totals.filter((total): total is FirmTotal => total !== null);
  const currencies = new Set(firm.map((total) => total.currency));
  const summable = companies.length > 0 && firm.length === totals.length && currencies.size === 1;
  const grand = summable
    ? { amount: firm.reduce((sum, total) => sum + total.amount, 0), currency: firm[0].currency }
    : null;
  return { perCountry, grand };
}

/**
 * One request per company, in checklist order. Every order goes by name:
 * the subject's own details fill the manual form, and Armadillo's desk
 * identifies the company from them.
 */
export function batchPayloads(
  reportId: string,
  companies: BatchCompany[],
  products: string[],
  notes: string
): RegistryOrderCreateRequest[] {
  return companies.map(({ subject, country }) =>
    buildOrderPayload(
      {
        reportId,
        subjectId: subject.id,
        country,
        mode: 'manual',
        candidate: null,
        manual: manualValuesFor(subject),
        products,
        notes,
      },
      false
    )
  );
}
